import React, { FormEvent, useEffect, useState } from 'react';

import Button, { LoadingButton } from '@atlaskit/button';
import { ErrorMessage, Field } from '@atlaskit/form';
import Modal, { ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Textfield from '@atlaskit/textfield';

import { ModalBody, ModalFooter } from '@qality/common-components';
import { useProjectContextValue } from '@qality/common-components/src/lib/QAlityPageConfiguration';
import RecentAndMoreProjectSelect from '@qality/common-components/src/select/RecentAndMoreProjectSelect';
import { Folder, ProjectSelectOption } from '@qality/common-model';
import {
  FlagService,
  FolderService,
  PermissionService,
} from '@qality/common-service';
import { StringUtils } from '@qality/common-utils';

import { CreateFolderProps } from '../TestCaseRepositoryModalsProvider/TestCaseRepositoryModalsProvider';
import {
  HeaderWrapperStyled,
  InfoWrapperStyled,
} from './CreateFolderModal.styled';

export const CREATE_ISSUE_PERMISSION_ERROR =
  'You do not have permission to create issues in this project.';
export const REQUIRED_FIELD_ERROR = 'This field is required.';
export const TOO_LONG_ERROR = 'This field should be between 1-255 characters.';

const CREATE_FOLDER_MESSAGE =
  'Visibility of this folder will be set based on permissions for the main project selected below.';
const CREATE_SUBFOLDER_MESSAGE =
  'Visibility of this subfolder will be set based on permissions for the main project selected for the parent folder.';

type Props = {
  onCreateFolder: (folder: Folder) => void;
  onClose: () => void;
  createFolderProps: CreateFolderProps;
};

export default function CreateFolderModal({
  onCreateFolder,
  onClose,
  createFolderProps: { testCasesIds, parentFolder },
}: Props): JSX.Element {
  const project = useProjectContextValue();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [name, setName] = useState<string>(undefined);
  const [nameError, setNameError] = useState<string>(null);

  const [selectedProject, setSelectedProject] =
    useState<ProjectSelectOption>(undefined);
  const [selectedProjectError, setSelectedProjectError] =
    useState<string>(null);

  const [isCheckingPermissions, setIsCheckingPermissions] =
    useState<boolean>(null);

  const folderType = parentFolder ? 'subfolder' : 'folder';

  const handleCreateFolder = async () => {
    const isProjectValid = validateProject();
    const isNameValid = validateName();

    if (isProjectValid && isNameValid) {
      setIsLoading(true);
      try {
        const newFolder = await FolderService.createFolder({
          name,
          testCasesIds,
          projectId: parentFolder?.projectId ?? String(selectedProject.value),
          parentId: parentFolder?.id,
        });

        onCreateFolder(newFolder);
        FlagService.createSuccessFlag(
          'Success',
          `The ${folderType} was created successfully.`
        );
        onClose();
      } catch {
        FlagService.createErrorFlag(
          'Error',
          `Sorry, we could not create the ${folderType}. Please try again.`
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleNameChange = (e: FormEvent<HTMLInputElement>) => {
    setName(e.currentTarget.value);
  };

  const handleProjectChange = (newProject: ProjectSelectOption) => {
    setSelectedProject(newProject);
  };

  const fetchPermission = async () => {
    setIsCheckingPermissions(true);

    const projectPermissions = await PermissionService.hasProjectPermissions(
      selectedProject.value,
      ['CREATE_ISSUES']
    );

    if (projectPermissions['CREATE_ISSUES']) {
      setSelectedProjectError(null);
    } else {
      setSelectedProjectError(CREATE_ISSUE_PERMISSION_ERROR);
    }

    setIsCheckingPermissions(false);
  };

  const validateProject = (): boolean => {
    if (!selectedProject) {
      setSelectedProjectError(REQUIRED_FIELD_ERROR);
      return false;
    }

    setSelectedProjectError(null);
    return true;
  };

  const validateName = (): boolean => {
    if (!name) {
      setNameError(REQUIRED_FIELD_ERROR);
      return false;
    }

    if (name.length > 255) {
      setNameError(TOO_LONG_ERROR);
      return false;
    }

    setNameError(null);
    return true;
  };

  useEffect(() => {
    if (selectedProject === undefined) {
      // skip first render
      return;
    }

    if (!selectedProject) {
      setSelectedProjectError(REQUIRED_FIELD_ERROR);
    } else {
      fetchPermission();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (name === undefined) {
      // skip first render
      return;
    }

    validateName();
  }, [name]);

  return (
    <ModalTransition>
      <Modal
        onClose={onClose}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEscapePress={true}
        shouldScrollInViewport
        width={400}
      >
        <HeaderWrapperStyled>
          <ModalTitle>Create {folderType}</ModalTitle>
        </HeaderWrapperStyled>
        <ModalBody>
          <Field
            label={`${StringUtils.capitalize(folderType)} name`}
            name='folder-name'
            isRequired
          >
            {({ fieldProps }) => (
              <>
                <Textfield
                  {...fieldProps}
                  onChange={handleNameChange}
                  isInvalid={!!nameError}
                />
                {nameError && <ErrorMessage>{nameError}</ErrorMessage>}
              </>
            )}
          </Field>
          <InfoWrapperStyled>
            {parentFolder ? CREATE_SUBFOLDER_MESSAGE : CREATE_FOLDER_MESSAGE}
          </InfoWrapperStyled>
          <Field label='Main project' isRequired name='main-project'>
            {() => (
              <>
                <RecentAndMoreProjectSelect
                  onChange={handleProjectChange}
                  useAllOption={false}
                  addNameOfFilter={false}
                  isDisabled={!!parentFolder}
                  tooltip={undefined}
                  showLabel={false}
                  isInvalid={!!selectedProjectError}
                  isInModal={true}
                  projectId={parentFolder?.projectId ?? project?.id}
                />
                {selectedProjectError && (
                  <ErrorMessage>{selectedProjectError}</ErrorMessage>
                )}
              </>
            )}
          </Field>
        </ModalBody>
        <ModalFooter>
          <Button appearance='subtle' onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            isLoading={isLoading || isCheckingPermissions}
            isDisabled={
              isCheckingPermissions || !!nameError || !!selectedProjectError
            }
            appearance='primary'
            onClick={handleCreateFolder}
          >
            Create new {folderType}
          </LoadingButton>
        </ModalFooter>
      </Modal>
    </ModalTransition>
  );
}
